import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import style from './LoginForm.module.css';
import { Body1, Body3 } from '../../atomic/text/Text';
import TextButton from '../../atomic/buttons/textButton/TextButton';
import { colors } from '../../../colorVars';

export const ForgotPasswordForm = observer(() => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setIsLoading(true);

    try {
      const baseUrl = window.config?.REACT_APP_REDIRECT || 'http://localhost:8080/';

      const response = await fetch(`${baseUrl}api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ username }),
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Username not found');
        }
        throw new Error('Failed to process request. Please try again.');
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={style.container}>
      <div className={style.formCard}>
        <Body1 color={colors.lightGrey} fontWeight={700}>
          Forgot Password
        </Body1>

        {!success ? (
          <form onSubmit={handleSubmit} className={style.form}>
            <div style={{ marginBottom: '20px' }}>
              <Body3 color={colors.lightGrey} fontWeight={400}>
                Enter your username and we'll send you instructions to reset your password.
              </Body3>
            </div>

            <div className={style.inputGroup}>
              <label htmlFor="username" className={style.label}>
                <Body3 color={colors.lightGrey} fontWeight={500}>
                  Username
                </Body3>
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoComplete="username"
                className={style.input}
                disabled={isLoading}
              />
            </div>

            {error && (
              <div className={style.errorMessage}>
                <Body3 color={colors.red} fontWeight={500}>
                  {error}
                </Body3>
              </div>
            )}

            <div className={style.buttons}>
              <TextButton
                type="submit"
                width="100%"
                fontSize="medium"
                disabled={isLoading}
              >
                {isLoading ? 'Sending...' : 'Send Reset Instructions'}
              </TextButton>
            </div>

            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <a
                href="/ui/login"
                style={{ color: colors.lightGrey, textDecoration: 'none', cursor: 'pointer' }}
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/ui/login');
                }}
              >
                <Body3 color={colors.lightGrey} fontWeight={400}>
                  Back to Login
                </Body3>
              </a>
            </div>
          </form>
        ) : (
          <div className={style.form}>
            <div className={style.successMessage} style={{ marginBottom: '20px' }}>
              <Body3 color={colors.green} fontWeight={500}>
                Password reset instructions have been sent to the email associated with your account.
              </Body3>
            </div>
            <div style={{ marginBottom: '20px' }}>
              <Body3 color={colors.lightGrey} fontWeight={400}>
                Please check your email and follow the instructions to reset your password.
              </Body3>
            </div>
            <div className={style.buttons}>
              <TextButton
                width="100%"
                fontSize="medium"
                clickHandler={() => navigate('/ui/login')}
              >
                Return to Login
              </TextButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});
